import regression from "regression";
import {
  SideOffsetMinPlotline,
  SideOffsetMaxPlotline,
  HeightOffsetMinPlotline,
  HeightOffsetMaxPlotline
} from "../../utils/groundTruthPlotLines";

// Soft red and blue and green
const RED = "#FF6D66";
const BLUE = "#63AAF1";
const GREEN = '#C0D890';

// Standard axises for differences in inches
const IN_MIN = -2.0;
const IN_MAX = 2.0;

const RADIUS_SIZE = 3;

const defaultNumberLabels = {
  format: "{value:.2f}"
};

export function getSymbol(type) {
  switch (type) {
    case "Cannon":
      return "circle";
    case "Human":
      return "square";
    default:
      return "triangle";
  }
}

export function toRegressionPts(data) {
  const pts = data.map(p => [p.x, p.y]);
  const result = regression.linear(pts);

  // We only need the starting and end point to include in the highcharts series
  // We want the regression line to be across the plot area, so this uses x
  // coordinates that are guaranteed to be outside of the xAxis bounds.
  return [result.predict(-10), result.predict(10)];
}

export function areValidNumbers(nums) {
  // In the future 0 may be a valid entry, but currently this indicates n/a
  return nums.filter(n => n && parseFloat(n) !== 0).length === nums.length;
}

/*
 Start of ground truth configurations

 For each chart series, we need a mapper and a validator.
 Since we have a common schema for all charts,
 the mapper is responsible for converting the values to highcharts format
 and the validator is responsible for filtering out bad records.

 The rest of the configuration should match the highcharts API specs.
 */
export const HeightOffsetSideBiasConfig = {
  id: "hosb",
  title: "Height Offset Side Bias",
  xAxis: {
    title: {
      text: "WSU Side (ft)"
    },
    min: -1.5,
    max: 1.5,
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "Test Data Height - WSU Height (in)"
    },
    min: IN_MIN,
    max: IN_MAX,
    labels: defaultNumberLabels,
    plotLines: [HeightOffsetMinPlotline, HeightOffsetMaxPlotline]
  },
  seriesConfigs: [
    {
      name: "TM Offset",
      color: RED,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_trackman_height_radar),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_trackman_height_radar])
        ),
      regressionLine: {
        name: "TM Offset Regression",
        type: "line",
        color: RED,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Optical Offset",
      color: BLUE,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_trackman_height_optical),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_trackman_height_optical])
        ),
      regressionLine: {
        name: "Optical Offset Regression",
        type: "line",
        color: BLUE,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Hawkeye Offset",
      color: GREEN,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_hawkeye_height),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_hawkeye_height])
        ),
      regressionLine: {
        name: "Hawkeye Offset Regression",
        type: "line",
        color: GREEN,
        mapper: data => toRegressionPts(data)
      }
    }
  ]
};

export const HeightOffsetHeightBiasConfig = {
  id: "hohb",
  title: "Height Offset Height Bias",
  xAxis: {
    title: {
      text: "WSU Height (ft)"
    },
    min: 1.0,
    max: 5.0,
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "Test Data Height - WSU Height (in)"
    },
    min: IN_MIN,
    max: IN_MAX,
    labels: defaultNumberLabels,
    plotLines: [HeightOffsetMinPlotline, HeightOffsetMaxPlotline]
  },
  seriesConfigs: [
    {
      name: "TM Offset",
      color: RED,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_trackman_height_radar),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_height, p.delta_trackman_height_radar])
        ),
      regressionLine: {
        name: "TM Offset Regression",
        type: "line",
        color: RED,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Optical Offset",
      color: BLUE,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_trackman_height_optical),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_height, p.delta_trackman_height_optical])
        ),
      regressionLine: {
        name: "Optical Offset Regression",
        type: "line",
        color: BLUE,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Hawkeye Offset",
      color: GREEN,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_hawkeye_height),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_height, p.delta_hawkeye_height])
        ),
      regressionLine: {
        name: "Hawkeye Offset Regression",
        type: "line",
        color: GREEN,
        mapper: data => toRegressionPts(data)
      }
    }
  ]
};

export const SideOffsetSideBiasConfig = {
  id: "sosb",
  title: "Side Offset Side Bias",
  xAxis: {
    title: {
      text: "WSU Side (ft)"
    },
    min: -1.5,
    max: 1.5,
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "Test Data Side - WSU Side (in)"
    },
    min: IN_MIN,
    max: IN_MAX,
    labels: defaultNumberLabels,
    plotLines: [SideOffsetMinPlotline, SideOffsetMaxPlotline]
  },
  seriesConfigs: [
    {
      name: "TM Offset",
      color: RED,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_trackman_side_radar),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p => areValidNumbers([p.wsu_side, p.delta_trackman_side_radar])),
      regressionLine: {
        name: "TM Offset Regression",
        type: "line",
        color: RED,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Optical Offset",
      color: BLUE,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_trackman_side_optical),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.delta_trackman_side_optical])
        ),
      regressionLine: {
        name: "Optical Offset Regression",
        type: "line",
        color: BLUE,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Hawkeye Offset",
      color: GREEN,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y:
            parseFloat(p.delta_hawkeye_side),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.delta_hawkeye_side])
        ),
      regressionLine: {
        name: "Hawkeye Offset Regression",
        type: "line",
        color: GREEN,
        mapper: data => toRegressionPts(data)
      }
    }
  ]
};

export const SideOffsetHeightBiasConfig = {
  id: "sohb",
  title: "Side Offset Height Bias",
  xAxis: {
    title: {
      text: "WSU Height (ft)"
    },
    min: 1,
    max: 5,
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "Test Data Side - WSU Side (in)"
    },
    min: IN_MIN,
    max: IN_MAX,
    labels: defaultNumberLabels,
    plotLines: [SideOffsetMinPlotline, SideOffsetMaxPlotline]
  },
  seriesConfigs: [
    {
      name: "TM Offset",
      color: RED,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_trackman_side_radar),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_trackman_side_radar])
        ),
      regressionLine: {
        name: "TM Offset Regression",
        type: "line",
        color: RED,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Optical Offset",
      color: BLUE,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_trackman_side_optical),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_trackman_side_optical])
        ),
      regressionLine: {
        name: "Optical Offset Regression",
        type: "line",
        color: BLUE,
        mapper: data => toRegressionPts(data)
      }
    },
    {
      name: "Hawkeye Offset",
      color: GREEN,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_height),
          y:
            parseFloat(p.delta_hawkeye_side),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([p.wsu_side, p.wsu_height, p.delta_hawkeye_side])
        ),
      regressionLine: {
        name: "Optical Offset Regression",
        type: "line",
        color: GREEN,
        mapper: data => toRegressionPts(data)
      }
    }
  ]
};

export const OffsetChartConfig = {
  id: "oc",
  width: 500,
  aspectRatio: 9 / 10,
  title: "Test Data - WSU Offset",
  xAxis: {
    title: {
      text: "Test Data Side - WSU Side (in)"
    },
    min: -2.0,
    max: 2.0,
    plotLines: [
      SideOffsetMinPlotline,
      SideOffsetMaxPlotline
    ],
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "Test Data Height - WSU Height (in)"
    },
    min: -2.0,
    max: 2.0,
    labels: defaultNumberLabels,
    plotLines: [HeightOffsetMinPlotline, HeightOffsetMaxPlotline]
  },
  seriesConfigs: [
    {
      name: "TM Offset",
      color: RED,
      mapper: p => {
        return {
          x:
            parseFloat(p.delta_trackman_side_radar),
          y:
            parseFloat(p.delta_trackman_height_radar),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([
            p.delta_trackman_side_radar,
            p.delta_trackman_height_radar
          ])
        )
    },
    {
      name: "Optical Offset",
      color: BLUE,
      mapper: p => {
        return {
          x:
            parseFloat(p.delta_trackman_side_optical),
          y:
            parseFloat(p.delta_trackman_height_optical),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([
            p.delta_trackman_side_optical,
            p.delta_trackman_height_optical
          ])
        )
    },
    {
      name: "Hawkeye Offset",
      color: GREEN,
      mapper: p => {
        return {
          x:
            parseFloat(p.delta_hawkeye_side),
          y:
            parseFloat(p.delta_hawkeye_height),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p =>
          areValidNumbers([
            p.delta_hawkeye_side,
            p.delta_hawkeye_height
          ])
        )
    }
  ]
};

export const WsuPitchLocationsConfig = {
  id: "wplc",
  title: "Pitch Locations (Catcher's Perspective)",
  addRender: true,
  width: 350,
  aspectRatio: 61 / 100,
  xAxis: {
    title: {
      text: "WSU Side (ft)"
    },
    min: -1.5,
    max: 1.5,
    labels: defaultNumberLabels
  },
  yAxis: {
    title: {
      text: "WSU Pitch Height (ft)"
    },
    min: 0,
    max: 4.5,
    labels: defaultNumberLabels
  },
  seriesConfigs: [
    {
      name: "Pitch",
      color: BLUE,
      mapper: p => {
        return {
          x: parseFloat(p.wsu_side),
          y: parseFloat(p.wsu_height),
          pitcherType: p.pitcher_type,
          marker: {
            symbol: getSymbol(p.pitcher_type),
            radius: RADIUS_SIZE
          }
        };
      },
      validator: data =>
        data.filter(p => areValidNumbers([p.wsu_side, p.wsu_height]))
    }
  ]
};


// WEBPACK FOOTER //
// ./src/components/charts/GroundTruthChartConfigs.js